import { ActionIcon, Box, Tooltip } from '@mantine/core';
import { IconLayoutSidebarRightExpand } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import { SessionToolsResizeHandle } from './SessionToolsResizeHandle';
import {
  SESSION_TOOLS_DEFAULT_WIDTH,
  SESSION_TOOLS_MOBILE_MEDIA_QUERY,
  clampSessionToolsWidth,
  persistSessionToolsWidth,
  readStoredSessionToolsWidth,
} from './sessionToolsDrawerConstants';

export interface SessionToolsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Page content that shrinks when the drawer is open (required — same as AiAssistantLayout). */
  main: ReactNode;
  /** Floating reopen control when closed. Default true. */
  showOpenButton?: boolean;
}

/**
 * Right session-tools drawer — mirrors AiAssistantLayout:
 * desktop width-sharing flex + invisible spacer + fixed viewport panel + resize handle.
 */
export function SessionToolsDrawer({
  open,
  onOpenChange,
  children,
  main,
  showOpenButton = true,
}: SessionToolsDrawerProps): JSX.Element {
  const isMobile = useMediaQuery(SESSION_TOOLS_MOBILE_MEDIA_QUERY) ?? false;
  const containerRef = useRef<HTMLDivElement>(null);
  const openButtonRef = useRef<HTMLButtonElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState(1200);
  const [width, setWidthState] = useReducer(
    (_prev: number, next: number) => next,
    undefined,
    readStoredSessionToolsWidth,
  );

  const setWidth = useCallback((next: number, availableMainWidth = Number.POSITIVE_INFINITY) => {
    const clamped = clampSessionToolsWidth(next, availableMainWidth);
    setWidthState(clamped);
    persistSessionToolsWidth(clamped);
  }, []);

  const resetWidth = useCallback(() => setWidth(SESSION_TOOLS_DEFAULT_WIDTH), [setWidth]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setAvailableWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const floatingOpenButton =
    showOpenButton && !open ? (
      <Tooltip label="Open session tools" position="left" withArrow>
        <ActionIcon
          ref={openButtonRef}
          variant="filled"
          color="gold"
          size={44}
          radius="md"
          aria-label="Open session tools"
          onClick={() => onOpenChange(true)}
          style={{
            position: 'fixed',
            top: '50%',
            right: 0,
            transform: 'translateY(-50%)',
            zIndex: 140,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <IconLayoutSidebarRightExpand size={22} />
        </ActionIcon>
      </Tooltip>
    ) : null;

  if (isMobile) {
    return (
      <Box ref={containerRef} style={{ position: 'relative', minWidth: 0, width: '100%' }}>
        {main}
        <Box
          style={{
            position: 'fixed',
            inset: 0,
            top: 60,
            zIndex: 150,
            background: 'var(--mantine-color-body)',
            transform: open ? 'translateX(0)' : 'translateX(100%)',
            opacity: open ? 1 : 0,
            visibility: open ? 'visible' : 'hidden',
            pointerEvents: open ? 'auto' : 'none',
            transition: open
              ? 'transform 180ms ease, opacity 180ms ease'
              : 'transform 180ms ease, opacity 180ms ease, visibility 0s linear 180ms',
          }}
          aria-hidden={!open}
        >
          {children}
        </Box>
        {floatingOpenButton}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        minWidth: 0,
        width: '100%',
        position: 'relative',
      }}
    >
      <Box
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          width: open ? `calc(100% - ${width}px)` : '100%',
          transition: 'width 180ms ease',
        }}
      >
        {main}
      </Box>
      {/* Invisible spacer reserves layout width so page content shrinks. */}
      <Box
        style={{
          width: open ? width : 0,
          flexShrink: 0,
          transition: 'width 180ms ease',
        }}
        aria-hidden
      />
      <Box
        style={{
          position: 'fixed',
          top: 60,
          right: 0,
          bottom: 0,
          width,
          zIndex: 100,
          display: 'flex',
          overflow: 'hidden',
          background: 'var(--mantine-color-body)',
          borderLeft: open ? '1px solid var(--mantine-color-gray-4)' : undefined,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: open ? 'auto' : 'none',
          transition: open
            ? 'transform 180ms ease, opacity 180ms ease'
            : 'transform 180ms ease, opacity 180ms ease, visibility 0s linear 180ms',
        }}
        aria-hidden={!open}
      >
        <SessionToolsResizeHandle
          width={width}
          availableMainWidth={availableWidth}
          onWidthChange={setWidth}
          onReset={resetWidth}
        />
        <Box style={{ flex: 1, minWidth: 0, height: '100%' }}>{children}</Box>
      </Box>
      {floatingOpenButton}
    </Box>
  );
}
